import React, { useCallback } from "react";
import { shallowEqual } from "react-redux";

import {
    addMultilineField,
    fillMultiline,
    removeMultilineField,
} from "../../../Engine/ActionCreators/AllActionCreators";
import { FullFormState } from "../../../Engine/Core/Store/FullFormState";
import { ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { isNodeOrChildrenContainsError, iterateViewModels } from "../../viewModelUtils";
import { withControlDebugInfo } from "../../withControlDebugInfo";
import { withResolvePaths } from "../../withResolvePaths";
import { withViewModelSelect } from "../../withViewModelSelect";
import { withExternalMountingEventReporting } from "../../Services/ExternalMountingEventReporting/withExternalMountingEventReporting";
import { withFocusManagement } from "../../Services/FocusManagement/withFocusManagement";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { ModelHunk } from "../../../Engine/Core/ModelStoring/ModelHunk";
import { EvaluableProp, withEvaluators } from "../../withEvaluators";
import { isNotNullOrEmpty } from "../../../../../Common/TypingUtils";
import { NodeViewModel } from "../../../Engine/Core/Store/FormViewModel";
import { useMultilineClient } from "../../../Engine/Core/Filtering/UseMultilineClient";
import { useTypedDispatch, useTypedSelector } from "../../../../../Common/UseTypedSelector";
import { createHocDisplayName } from "../../TypingUtils";

import { MultiLineField, MultiLineFieldProps } from "./MultiLineField";

interface MultiLineFieldContainerOwnProps {
    bindingPath: AbsoluteResolvedModelPath;
    parentPath?: string;
    viewModelSelect: ViewModelSelect;
    disableButtons?: boolean;
    filteredUniqKey?: string;
    disableButtons2?: EvaluableProp<boolean>;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
    maxOccurs?: number;
    children: React.ReactNode;
    hintOnAdd: string | undefined;
    pluralTitles: [string, string, string] | undefined;
}

export type MultiLineFieldContainerProps = MultiLineFieldContainerOwnProps &
    Omit<
        Omit<
            Omit<MultiLineFieldProps, keyof ReturnType<typeof mapStateToProps>>,
            keyof ReturnType<typeof useMapDispatchToProps>
        >,
        "hintOnAdd" | "pluralTitles"
    >;

const mapStateToProps = (
    state: FullFormState,
    {
        bindingPath,
        viewModelSelect,
        disableButtons,
        disableButtons2,
        processEvaluableProp,
        maxOccurs,
    }: MultiLineFieldContainerOwnProps
) => {
    const field = viewModelSelect.node(state, bindingPath);
    const error = isNodeOrChildrenContainsError(field);
    const instances = viewModelSelect.children(state, bindingPath, ["0"]);

    const currentNode = viewModelSelect.node(state, bindingPath);
    const { "0": firstNode, ...anotherNodes } = currentNode;

    const hasDataInFirstNode = firstNode
        ? Array.from(iterateViewModels(firstNode as NodeViewModel)).some(x => isNotNullOrEmpty(x.value))
        : false;

    const hasDataInOtherNodes = anotherNodes
        ? Array.from(iterateViewModels(anotherNodes)).some(x => isNotNullOrEmpty(x.value))
        : false;

    const disableAppend = maxOccurs != undefined && instances.length >= maxOccurs;
    const disableButtonsProcessed = processEvaluableProp(state, disableButtons2) || disableButtons;
    return {
        value: viewModelSelect.controlModel(state, bindingPath).value,
        disableAppend: disableAppend,
        disableButtons: disableButtonsProcessed,
        isReadOnly: state.settings.isReadOnly,
        error: error,
        instances: instances,
        hasDataInFirstNode: hasDataInFirstNode,
        hasDataInOtherNodes: hasDataInOtherNodes,
    };
};

const useMapDispatchToProps = (bindingPath: AbsoluteResolvedModelPath) => {
    const dispatch = useTypedDispatch();
    return {
        handleFillMultiline: useCallback(
            (values: ModelHunk, newInstanceCount: number) => {
                dispatch(fillMultiline(bindingPath, values, newInstanceCount));
            },
            [dispatch, bindingPath]
        ),

        handleAddInstance: useCallback(() => {
            dispatch(addMultilineField(bindingPath));
        }, [dispatch, bindingPath]),

        handleRemoveInstance: useCallback(
            (instance: string) => {
                dispatch(removeMultilineField(bindingPath, instance));
            },
            [dispatch, bindingPath]
        ),
    };
};

const WithMultiLineFieldWrapper = (ownProps: MultiLineFieldContainerProps) => {
    const { bindingPath, filteredUniqKey = "default", children } = ownProps;

    const { filters } = useMultilineClient(bindingPath, filteredUniqKey, info => ({
        filters: info.multilineState.filters,
    }));
    const filtered = Boolean(filters && Object.keys(filters).some(key => key.startsWith(mask)));

    const mask = bindingPath.toLegacyPath().replace(/\/[0-9]\//g, "/*/");

    const multilineFieldProps = useTypedSelector(state => mapStateToProps(state, ownProps), shallowEqual);

    const actions = useMapDispatchToProps(bindingPath);

    return (
        <MultiLineField
            {...ownProps}
            {...multilineFieldProps}
            bindingPath={bindingPath}
            filtered={filtered}
            fillMultiline={actions.handleFillMultiline}
            addInstance={actions.handleAddInstance}
            removeInstance={actions.handleRemoveInstance}>
            {children}
        </MultiLineField>
    );
};
WithMultiLineFieldWrapper.displayName = createHocDisplayName(MultiLineField, "MultiLineFieldWrapper");

export const MultiLineFieldContainer = withResolvePaths(
    withViewModelSelect(
        withEvaluators(
            withFocusManagement(withExternalMountingEventReporting(withControlDebugInfo(WithMultiLineFieldWrapper)))
        )
    )
);
